import { useState, useEffect } from 'react';
import { logsAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle } from 'lucide-react';
import { GUIDE_ROW_LABELS, STAGE_LABELS } from './autopsyConstants';
import AutopsyCoverageChart from './AutopsyCoverageChart';
import InjectionManifestChips from './InjectionManifestChips';
import { fechaYHora } from '../../lib/fechas';

export default function AutopsyConfigReport({ conversationId, viewMode = 'report' }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [turnIdx, setTurnIdx] = useState(0);

  useEffect(() => {
    if (!conversationId) {
      setReport(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    logsAPI.configReport(conversationId)
      .then((res) => {
        if (!cancelled) {
          setReport(res.data);
          setTurnIdx(Math.max(0, (res.data?.turns?.length || 1) - 1));
        }
      })
      .catch(() => { if (!cancelled) setReport(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [conversationId]);

  if (!conversationId) {
    return <p className="text-xs text-zinc-400 text-center py-8">Selecciona una conversacion</p>;
  }
  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;
  }
  if (!report?.turns?.length) {
    return <p className="text-xs text-zinc-400 text-center py-8">Sin turnos registrados (envia mensajes con logging activo)</p>;
  }

  const turn = report.turns[turnIdx] || report.turns[0];
  const summary = report.summary || {};
  const never = summary.guides_never_injected || [];
  const stageKeys = Object.keys(STAGE_LABELS);
  const guideKeys = Object.keys(GUIDE_ROW_LABELS);

  if (viewMode === 'coverage') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase text-zinc-400">Turno</span>
          <select
            className="border border-brand-border rounded-sm h-8 px-2 text-xs"
            value={turnIdx}
            onChange={(e) => setTurnIdx(Number(e.target.value))}
          >
            {report.turns.map((t, i) => (
              <option key={i} value={i}>
                Turno {t.turn_index} · {t.message_received_at ? fechaYHora(t.message_received_at) : '—'}
              </option>
            ))}
          </select>
          {report.capsule_id && <Badge variant="outline" className="text-[9px]">{report.capsule_id}</Badge>}
        </div>
        <AutopsyCoverageChart turn={turn} />
        {stageKeys.map((sk) => {
          const sd = turn.stages[sk];
          if (!sd?.injection_manifest) return null;
          return (
            <div key={sk} className="border border-zinc-100 rounded-sm p-2">
              <p className="text-[10px] font-semibold text-zinc-500 mb-1">{STAGE_LABELS[sk]}</p>
              <InjectionManifestChips manifest={sd.injection_manifest} compact />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase text-zinc-400">Turno</span>
          <select
            className="border border-brand-border rounded-sm h-8 px-2 text-xs"
            value={turnIdx}
            onChange={(e) => setTurnIdx(Number(e.target.value))}
          >
            {report.turns.map((t, i) => (
              <option key={i} value={i}>
                Turno {t.turn_index} · {t.message_received_at ? fechaYHora(t.message_received_at) : '—'}
              </option>
            ))}
          </select>
          {report.capsule_id && <Badge variant="outline" className="text-[9px]">{report.capsule_id}</Badge>}
        </div>
        <div className="text-[10px] text-zinc-500">
          Guías aplicadas: <strong>{summary.guides_applied_count ?? 0}</strong> / {guideKeys.length}
        </div>
      </div>

      {never.length > 0 && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[10px] text-amber-900">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <strong>Guías no inyectadas en esta conversación:</strong>{' '}
            {never.map((g) => GUIDE_ROW_LABELS[g] || g).join(', ')}
            <span className="block text-amber-700 mt-0.5">
              {summary.kb_gate_disabled && 'Gate KB en off: «Uso KB» no aplica (RAG directo). '}
              Puede deberse a guía vacía, etapa no alcanzada (p. ej. escalamiento con &lt;4 mensajes, sin ticket → sin post-turno) o conversación anterior al despliegue de autopsia v2.
            </span>
          </div>
        </div>
      )}
      {summary.kb_gate_disabled && never.length === 0 && (
        <p className="text-[10px] text-zinc-500 p-2 bg-zinc-50 border border-zinc-200 rounded-sm">
          <strong>kb_retrieval_gate=off:</strong> la guía «Uso KB» no se evalúa; el agente con skill knowledge-search ejecuta RAG vectorial directamente (sin evento kb_gate_decision).
        </p>
      )}

      {summary.runtime_modes && Object.keys(summary.runtime_modes).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {Object.entries(summary.runtime_modes).map(([k, v]) => (
            <Badge key={k} variant="outline" className="text-[9px] font-mono">{k}={String(v)}</Badge>
          ))}
        </div>
      )}

      <div className="overflow-auto border border-brand-border rounded-sm">
        <table className="w-full text-[10px] min-w-[640px]">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left p-2 font-semibold text-zinc-500">Fuente config</th>
              {stageKeys.map((sk) => (
                <th key={sk} className="p-2 font-semibold text-zinc-500 text-center">{STAGE_LABELS[sk]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {guideKeys.map((gk) => (
              <tr key={gk} className="border-b border-zinc-100">
                <td className="p-2 font-medium text-zinc-700">{GUIDE_ROW_LABELS[gk]}</td>
                {stageKeys.map((sk) => {
                  const stageData = turn.stages[sk];
                  const manifest = stageData?.injection_manifest;
                  const block = (manifest?.blocks || []).find((b) => (b.source || '').includes(gk.replace('_guidance', '')));
                  if (block) {
                    return (
                      <td key={sk} className="p-2 text-center">
                        {block.injected ? (
                          <Badge className="text-[8px] bg-emerald-50 text-emerald-800">{block.chars}c</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[8px] text-zinc-400">vacio</Badge>
                        )}
                      </td>
                    );
                  }
                  const covered = (turn.config_coverage?.[gk] || []).includes(sk);
                  return (
                    <td key={sk} className="p-2 text-center text-zinc-300">
                      {covered ? '·' : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-b border-zinc-100 bg-zinc-50/50">
              <td className="p-2 font-medium text-zinc-700">SOUL / RULES / Habilidades</td>
              {stageKeys.map((sk) => (
                <td key={sk} className="p-2 text-center">
                  {(turn.stages[sk]?.injection_manifest?.blocks || []).filter((b) => b.injected && (b.source || '').startsWith('agent_configs')).length > 0 ? (
                    <Badge variant="outline" className="text-[8px]">si</Badge>
                  ) : (turn.stages[sk]?.timestamp ? '·' : '—')}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
